import React, { useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { Modal, Button, Form } from "react-bootstrap";
import { IoCloseSharp, IoCropSharp } from "react-icons/io5";
import SwitchToggle from "../Common/SwitchToggle";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";
import { parseRatio, uploadFileToS3 } from "../../utils/common";

export default function AlbumEmbedModal({
  show,
  onClose,
  userData,
  setUserData,
  albumEmbedFormData,
  setAlbumEmbedFormData,
}) {
  const { setLoading } = useLoader();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const activeMediaFileRef = useRef(null);
  const [imageMediaError, setImageMediaError] = useState("");
  const [videoMediaError, setVideoMediaError] = useState("");
  const [imageSrc, setImageSrc] = useState(null);
  const [editingImageSrc, setEditingImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedArea, setCroppedArea] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropMediaType, setCropMediaType] = useState(null);

  const handleClear = () => {
    if (imageInputRef.current) {
      setImageMediaError("");
      imageInputRef.current.value = "";
    }
    if (videoInputRef.current) {
      setVideoMediaError("");
      videoInputRef.current.value = "";
    }
    activeMediaFileRef.current = null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAlbumEmbedFormData({ ...albumEmbedFormData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      setImageMediaError("Only image allowed");
      imageInputRef.current.value = "";
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const uploadedRatio = img.width / img.height;
      const expectedRatio = parseRatio(albumEmbedFormData?.ratio);

      // Allow small floating difference
      if (Math.abs(uploadedRatio - expectedRatio) > 0.02) {
        // Show inline cropper to allow user to crop to expected ratio
        setImageMediaError("");
        setImageSrc(img.src);
        setCropMediaType("image");
        setIsCropping(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        // keep the input value so user can cancel or crop
        return;
      }

      if (imageMediaError) setImageMediaError("");
      activeMediaFileRef.current = file;
      setAlbumEmbedFormData((prev) => ({
        ...prev,
        originalMediaFile: file,
        mediaType: "image",
        localPreview: img.src,
      }));
    };
  };

  const onCropComplete = (croppedArea, croppedPixels) => {
    setCroppedArea(croppedArea);
    setCroppedAreaPixels(croppedPixels);
  };

  const getCroppedImg = async (imageSrcArg, cropPixels) => {
    const image = new Image();
    image.src = imageSrcArg;
    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;

    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      cropPixels.width,
      cropPixels.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        resolve(file);
      }, "image/jpeg");
    });
  };

  const getCroppedVideoFile = async (videoSrcArg, cropPixels) => {
    if (!videoSrcArg || !cropPixels) {
      throw new Error("Video or crop area is missing.");
    }

    const video = document.createElement("video");
    video.src = videoSrcArg;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";

    await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        resolve();
      };
      video.onerror = () =>
        reject(new Error("Unable to load video for cropping."));
    });

    const outputWidth = Math.max(1, Math.round(cropPixels.width));
    const outputHeight = Math.max(1, Math.round(cropPixels.height));
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext("2d");

    const stream = canvas.captureStream(30);

    // Keep the original soundtrack: captureStream() on the canvas is video-only.
    try {
      const captureAudio =
        video.captureStream?.bind(video) ||
        video.mozCaptureStream?.bind(video);
      captureAudio?.()
        ?.getAudioTracks()
        ?.forEach((track) => stream.addTrack(track));
    } catch (e) {}

    const chunks = [];
    // H.264/MP4 first — iOS Safari cannot decode the VP8/VP9 WebM that
    // Chrome and Firefox default to, and shows a placeholder instead.
    const mimeType =
      [
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/mp4;codecs=avc1",
        "video/mp4",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
      ].find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || "";
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const drawFrame = () => {
      if (!video || !ctx) return;

      if (video.readyState >= 2) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          video,
          cropPixels.x,
          cropPixels.y,
          cropPixels.width,
          cropPixels.height,
          0,
          0,
          canvas.width,
          canvas.height
        );
      }

      if (video.ended || video.currentTime >= video.duration) {
        recorder.stop();
        return;
      }

      requestAnimationFrame(drawFrame);
    };

    const playbackPromise = new Promise((resolve, reject) => {
      video.onended = () => {
        resolve();
      };
      video.onerror = () =>
        reject(new Error("Failed to play video for cropping."));
    });

    const filePromise = new Promise((resolve, reject) => {
      recorder.onstop = () => {
        try {
          const blob = new Blob(chunks, {
            type: recorder.mimeType || mimeType || "video/webm",
          });
          // The extension has to match the real container: B2 derives the
          // Content-Type from it, and a mislabelled file will not play.
          const extension = blob.type.includes("mp4") ? "mp4" : "webm";
          const file = new File([blob], `cropped-video.${extension}`, {
            type: blob.type,
          });
          resolve(file);
        } catch (err) {
          reject(err);
        }
      };
      recorder.onerror = () =>
        reject(new Error("Failed to export cropped video."));
    });

    recorder.start();
    video.currentTime = 0;
    await video.play();
    drawFrame();
    await playbackPromise;

    if (recorder.state !== "inactive") {
      recorder.stop();
    }

    return filePromise;
  };

  const handleCropSave = async () => {
    try {
      if (!croppedAreaPixels || !imageSrc) {
        return false;
      }
      if (cropMediaType === "video") {
        const croppedFile = await getCroppedVideoFile(
          imageSrc,
          croppedAreaPixels
        );
        const previewUrl = URL.createObjectURL(croppedFile);
        activeMediaFileRef.current = croppedFile;
        setAlbumEmbedFormData((prev) => {
          try {
            if (
              prev?.localPreview &&
              String(prev.localPreview).startsWith("blob:")
            ) {
              URL.revokeObjectURL(prev.localPreview);
            }
          } catch (e) {}
          return {
            ...prev,
            originalMediaFile: croppedFile,
            mediaType: "video",
            localPreview: previewUrl,
            videoCropData: croppedArea,
          };
        });
      } else {
        const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
        const previewUrl = URL.createObjectURL(croppedFile);
        activeMediaFileRef.current = croppedFile;
        setAlbumEmbedFormData((prev) => {
          try {
            if (
              prev?.localPreview &&
              String(prev.localPreview).startsWith("blob:")
            ) {
              URL.revokeObjectURL(prev.localPreview);
            }
          } catch (e) {}
          return {
            ...prev,
            originalMediaFile: croppedFile,
            mediaType: "image",
            localPreview: previewUrl,
          };
        });
      }
      // cleanup only the temporary editing URL (if any)
      if (editingImageSrc) {
        URL.revokeObjectURL(editingImageSrc);
        setEditingImageSrc(null);
      } else if (imageSrc && imageSrc.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(imageSrc);
        } catch (e) {}
      }
      setImageSrc(null);
      setIsCropping(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to crop media.");
      return false;
    }
  };

  const handleCropCancel = () => {
    if (editingImageSrc) {
      try {
        URL.revokeObjectURL(editingImageSrc);
      } catch (e) {}
      setEditingImageSrc(null);
    } else if (imageSrc && imageSrc.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(imageSrc);
      } catch (e) {}
    }
    setImageSrc(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleEditPreviewImage = () => {
    if (albumEmbedFormData?.originalMediaFile instanceof File) {
      const tempUrl = URL.createObjectURL(albumEmbedFormData.originalMediaFile);
      setEditingImageSrc(tempUrl);
      setImageSrc(tempUrl);
    } else if (albumEmbedFormData?.localPreview) {
      setImageSrc(albumEmbedFormData.localPreview);
    } else if (albumEmbedFormData?.originalMediaLink) {
      setImageSrc(albumEmbedFormData.originalMediaLink);
    }
    setIsCropping(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("video/")) {
      setVideoMediaError("Only video allowed");
      videoInputRef.current.value = "";
      return;
    }

    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const uploadedRatio = video.videoWidth / video.videoHeight;
      const expectedRatio = parseRatio(albumEmbedFormData?.ratio);

      if (Math.abs(uploadedRatio - expectedRatio) > 0.02) {
        setVideoMediaError("");
        setImageSrc(video.src);
        setCropMediaType("video");
        setIsCropping(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        return;
      }

      if (videoMediaError) setVideoMediaError("");
      activeMediaFileRef.current = file;
      setAlbumEmbedFormData((prev) => ({
        ...prev,
        originalMediaFile: file,
        mediaType: "video",
        localPreview: video.src,
      }));
    };
  };

  const submitForm = async () => {
    try {
      setLoading(true);
      let currentFormData = { ...albumEmbedFormData };
      if (isCropping) {
        const cropSuccess = await handleCropSave();
        if (!cropSuccess) return;

        currentFormData.mediaType = cropMediaType;
        if (cropMediaType === "video") {
          currentFormData.videoCropData = croppedArea;
        }
      }

      if (
        !activeMediaFileRef.current &&
        !currentFormData.originalMediaFile &&
        !currentFormData.originalMediaLink
      ) {
        setImageMediaError("Please upload media file.");
        return;
      } else {
        if (imageMediaError) setImageMediaError("");
        if (videoMediaError) setVideoMediaError("");
      }

      let originalMediaLink = currentFormData.originalMediaLink || "";
      let embedLink = currentFormData.embedLink || "";

      if (
        embedLink &&
        !embedLink.startsWith("http://") &&
        !embedLink.startsWith("https://")
      ) {
        embedLink = "https://" + embedLink;
      }

      // Upload the currently active media file so the cropped version is saved
      const mediaFileToUpload =
        activeMediaFileRef.current || currentFormData.originalMediaFile;
      if (mediaFileToUpload) {
        originalMediaLink = await uploadFileToS3(mediaFileToUpload);
      }
      const response = await axiosUserInstance.post(
        currentFormData?._id ? `/widget/edit` : `/widget/add`,
        {
          type: "albumEmbed",
          ...currentFormData,
          originalMediaLink,
          embedLink,
          _id: currentFormData?._id || undefined,
        }
      );
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
        }));
        setAlbumEmbedFormData({
          lightWeightMediaLink: "",
          originalMediaLink: "",
          mediaType: "",
          isLinkEmbed: false,
          embedLink: "",
          ratio: "1:1",
        });
        onClose();
        toast.success("Album created successfully.");
      } else {
        toast.error(
          response?.message || "Album creation failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      backdrop="static"
      className="countdown-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="mb-0">
          {albumEmbedFormData?.ratio} Panel
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {(albumEmbedFormData?.localPreview ||
            albumEmbedFormData?.originalMediaLink ||
            (isCropping && imageSrc)) && (
            <div className="mb-3">
              <p className="mb-1">Uploaded file preview:</p>
              {isCropping && imageSrc ? (
                <div>
                  <div
                    style={{
                      position: "relative",
                      height: 400,
                      background: "#111",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <Cropper
                      image={cropMediaType === "image" ? imageSrc : undefined}
                      video={cropMediaType === "video" ? imageSrc : undefined}
                      crop={crop}
                      zoom={zoom}
                      aspect={parseRatio(albumEmbedFormData?.ratio)}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="flex-grow-1">
                      <label
                        style={{
                          marginRight: 8,
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        Zoom
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCropSave}
                    >
                      Save Crop
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCropCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="position-relative">
                  <button
                    onClick={() => {
                      if (albumEmbedFormData.localPreview) {
                        URL.revokeObjectURL(albumEmbedFormData.localPreview);
                      }
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        localPreview: null,
                        originalMediaFile: null,
                        lightWeightMediaLink: null,
                        originalMediaLink: null,
                      }));
                      handleClear();
                    }}
                    type="button"
                    className="position-absolute top-0 end-0 bg-white rounded-circle shadow"
                    style={{
                      width: "24px",
                      height: "24px",
                      border: "none",
                      zIndex: 99,
                    }}
                    aria-label="Close"
                  >
                    <IoCloseSharp />
                  </button>
                  <div className="object-fit-content">
                    {albumEmbedFormData?.mediaType === "video" ? (
                      albumEmbedFormData?.localPreview ? (
                        <video
                          src={albumEmbedFormData?.localPreview}
                          muted
                          autoPlay
                          loop
                          playsInline
                          controls={false}
                          className="rounded"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                          }}
                        />
                      ) : (
                        <video
                          width="100%"
                          height="auto"
                          autoPlay
                          loop
                          controls
                        >
                          <source src={albumEmbedFormData?.originalMediaLink} />
                        </video>
                      )
                    ) : (
                      <>
                        <img
                          src={
                            albumEmbedFormData?.localPreview ||
                            albumEmbedFormData?.originalMediaLink
                          }
                          alt="Album preview"
                          className="object-fit"
                        />
                        {albumEmbedFormData?.mediaType === "image" && (
                          <button
                            onClick={handleEditPreviewImage}
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{
                              position: "absolute",
                              top: "8px",
                              left: "8px",
                              zIndex: 50,
                              padding: "4px 6px",
                              borderRadius: "4px",
                              width: "fit-content",
                            }}
                          >
                            <IoCropSharp size={20} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mb-3">
            <label>Add Image Preview</label>
            <Form.Control
              type="file"
              accept="image/*, .webp"
              ref={imageInputRef}
              onChange={handleImageChange}
            />
            {imageMediaError && (
              <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                {imageMediaError}
              </div>
            )}
          </div>
          <div className="mb-3">
            {userData?.planType?.toLowerCase() === "free" && (
              <div
                style={{ color: "red", fontSize: "14px", marginBottom: "5px" }}
              >
                Upgrade to embed a video as panel
              </div>
            )}
            <label>Add Video Preview</label>
            <Form.Control
              type="file"
              accept="video/*, .webm, .webp"
              ref={videoInputRef}
              onChange={handleVideoChange}
              disabled={userData?.planType?.toLowerCase() === "free"}
              title={
                userData?.planType?.toLowerCase() !== "yearly"
                  ? "Only yearly plan users can add video preview"
                  : ""
              }
            />
            {videoMediaError && (
              <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                {videoMediaError}
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <label
                className="me-2"
                style={{ fontSize: "14px", whiteSpace: "nowrap" }}
              >
                Link Embed
              </label>
              <SwitchToggle
                checked={albumEmbedFormData?.isLinkEmbed}
                onChange={() => {
                  if (albumEmbedFormData?.mediaType === "video") {
                    if (userData?.planType?.toLowerCase() === "yearly") {
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        isLinkEmbed: !prev.isLinkEmbed,
                      }));
                    } else {
                      toast.info(`Yearly plan is allow to unable embed link.`);
                    }
                  } else if (albumEmbedFormData?.mediaType === "image") {
                    if (userData?.planType?.toLowerCase() !== "free") {
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        isLinkEmbed: !prev.isLinkEmbed,
                      }));
                    } else {
                      toast.info(
                        `Free plan is not allow to unable embed link.`
                      );
                    }
                  } else {
                    toast.info(`First of upload image or video.`);
                  }
                }}
              />
            </div>
            <div className="w-100">
              <Form.Control
                type="text"
                name="embedLink"
                placeholder="Ex. https://biomeee.com/"
                value={albumEmbedFormData.embedLink}
                onChange={handleChange}
                disabled={!albumEmbedFormData.isLinkEmbed}
              />
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={submitForm}>
          {albumEmbedFormData?._id ? `Update` : `Save`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
